/**
 * Attribute Field Component
 *
 * A unified component for rendering attribute form controls.
 * Handles all field types (text, number, select, multiselect, date) and
 * automatically manages options loading for select/multiselect fields.
 *
 * Features:
 * - Automatic field type detection based on attribute metadata
 * - Lazy loading of options when dropdown opens
 * - Loading state indication
 * - Optional remove button
 * - Consistent styling across all attribute fields
 *
 * @example
 * ```html
 * <app-attribute-field
 *   [attribute]="attribute"
 *   [control]="form.get(attribute.code)"
 *   [showRemoveButton]="true"
 *   (remove)="onRemove(attribute.code)"
 * />
 * ```
 */
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';

import { ButtonModule } from '@progress/kendo-angular-buttons';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { LabelModule } from '@progress/kendo-angular-label';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';

import { AttributeOptionsFacade } from '../../model/attribute-options.facade';
import type { AttributeOption } from '../../model/attribute.model';

/**
 * Attribute definition for the field component.
 * Matches the shape used across link creation/editing.
 */
export interface AttributeFieldDefinition {
  uuid: string;
  code: string;
  name: string;
  fieldType?: string;
  required?: boolean;
  description?: string | null;
  /** Inline options (if already loaded by parent) */
  options?: Array<{ label: string; value: string }>;
}

/** Control type for rendering */
type AttributeControlType = 'text' | 'number' | 'select' | 'multiselect' | 'date';

@Component({
  selector: 'app-attribute-field',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    ButtonModule,
    InputsModule,
    DropDownsModule,
    DateInputsModule,
    LabelModule,
    IndicatorsModule,
  ],
  templateUrl: './attribute-field.component.html',
  styleUrl: './attribute-field.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'attribute-field',
  },
})
export class AttributeFieldComponent {
  private readonly optionsFacade = inject(AttributeOptionsFacade);
  private readonly destroyRef = inject(DestroyRef);

  // ===========================================================================
  // Inputs
  // ===========================================================================

  /** Attribute definition */
  readonly attribute = input.required<AttributeFieldDefinition>();

  /** Form control for the attribute value */
  readonly control = input.required<FormControl>();

  /** Whether to show the label */
  readonly showLabel = input(true);

  /** Whether to show the remove button */
  readonly showRemoveButton = input(false);

  // ===========================================================================
  // Outputs
  // ===========================================================================

  /** Emitted when remove button is clicked */
  readonly remove = output<void>();

  // ===========================================================================
  // Internal State
  // ===========================================================================

  /** Options loaded from the facade (for attributes without inline options) */
  private readonly facadeOptions = signal<AttributeOption[]>([]);

  /** Loading state from the facade */
  private readonly facadeLoading = signal<boolean>(false);

  // ===========================================================================
  // Computed Signals
  // ===========================================================================

  /** Determine the control type based on fieldType */
  protected readonly controlType = computed<AttributeControlType>(() => {
    const attr = this.attribute();
    const fieldType = (attr.fieldType ?? '').toUpperCase();

    switch (fieldType) {
      case 'NUMBER':
      case 'NUMBER_FORMATTED':
        return 'number';
      case 'SELECT':
      case 'DROPDOWN_SELECT':
        return 'select';
      case 'CHECKBOX':
      case 'MULTIPLE_CHECKBOX':
        return 'multiselect';
      case 'DATE':
      case 'DATEPICKER':
        return 'date';
      default:
        return 'text';
    }
  });

  /** Whether this is a field type that needs options */
  protected readonly needsOptions = computed(() => {
    const type = this.controlType();

    return type === 'select' || type === 'multiselect';
  });

  /**
   * Options for select/multiselect fields.
   * Uses inline options if provided, otherwise uses facade options.
   */
  protected readonly options = computed(() => {
    const attr = this.attribute();

    // Use inline options if provided
    if (attr.options && attr.options.length > 0) {
      return attr.options;
    }

    // Use options loaded from facade
    return this.facadeOptions();
  });

  /** Loading state for options */
  protected readonly isLoading = computed(() => {
    const attr = this.attribute();

    // If inline options provided, never loading
    if (attr.options && attr.options.length > 0) {
      return false;
    }

    return this.facadeLoading();
  });

  // ===========================================================================
  // Constructor
  // ===========================================================================

  constructor() {
    // Subscribe to attribute changes and sync facade options/loading state.
    // This uses toObservable to convert the input signal to an observable,
    // then switches to the facade observables for options and loading.
    const attribute$ = toObservable(this.attribute);

    // Subscribe to options changes
    attribute$.pipe(
      filter(attr => !attr.options?.length), // Only if no inline options
      distinctUntilChanged((prev, curr) => prev.uuid === curr.uuid),
      switchMap(attr => this.optionsFacade.selectOptions$(attr.uuid)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(options => {
      this.facadeOptions.set(options);
    });

    // Subscribe to loading state changes
    attribute$.pipe(
      filter(attr => !attr.options?.length), // Only if no inline options
      distinctUntilChanged((prev, curr) => prev.uuid === curr.uuid),
      switchMap(attr => this.optionsFacade.selectLoading$(attr.uuid)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(loading => {
      this.facadeLoading.set(loading);
    });
  }

  // ===========================================================================
  // Event Handlers
  // ===========================================================================

  /**
   * Handle dropdown open - load options if not already loaded.
   */
  protected onDropdownOpen(): void {
    const attr = this.attribute();

    // Skip if inline options are provided
    if (attr.options && attr.options.length > 0) {
      return;
    }

    // Load options via facade (handles caching internally)
    this.optionsFacade.loadOptions(attr.uuid);
  }

  /**
   * Handle remove button click.
   */
  protected onRemove(): void {
    this.remove.emit();
  }
}
